/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Address, AddressType, EditableAddress, PrintableAddress } from '@/data/types/Address';
import { Country, State } from '@/data/types/CountryState';
import { BasicAddress } from '@/data/types/Order';
import { PersonContact } from '@/data/types/Person';
import { REGEX as REG_EX } from '@/data/constants/regex';
import { getCityStreet } from '@/data/Content/AddressBook';
import { EMPTY_STRING } from '@/data/constants/marketing';
export { REG_EX };

export const ADDRESS_SHIPPING = 'Shipping';
export const ADDRESS_BILLING = 'Billing';
export const ADDRESS_SHIPPING_BILLING = 'ShippingAndBilling';

export const PrintableAttrs = [
	'fullName',
	'street',
	'physical',
	//'addressLine',
	//'city',
	//'country',
	'phone1',
	'email1',
];

export const PrintableAttrs2 = [
	'fullName',
	'street',
	'address2',
	'physical',
	'Nederland'
];

export const storePrintableAttrs = [
	'fullName',
	//'city',	
	'street',
	'physical',
	'phone1',
];
export const ShippingTablePrintableAttrs = ['addressLine', 'physical', 'country'];

export const ADDRESS_INIT: EditableAddress = {
	firstName: '',
	lastName: '',
	city: '',
	state: '', //Defaulting state to 'NA' as this is mandatory and hidden in address form
	zipCode: '',
	country: 'NL', //Defaulting country to NL as this is mandatory and hidden in address form
	phone1: '',
	nickName: '',
	email1: '',
	addressType: ADDRESS_SHIPPING_BILLING,
	addressLine1: '',
	addressLine2: '',
	addressLine3: '',
};

export const mapCountryStateOption = (options: State[] | Country[]) =>
	options.map((option) => option.displayName);

export const validateInput = (type: keyof typeof REG_EX, value: string) => {
	const stringType = REG_EX[type];
	return value !== undefined && value.trim() !== '' && stringType.test(value);
};

/**
 * @param _addr
 * @returns true if `_addr` has a non-empty country field and non-empty first address-line
 */
export const validateAddress = (
	_address:
		| (BasicAddress & { address1?: string })
		| (PersonContact & { address1?: string })
		| undefined
) => Boolean(_address?.country && (_address.addressLine?.at(0) || _address.address1));

export const makeEditable = (address: Address): EditableAddress => {
	const {
		addressLine,
		firstName = '',
		lastName = '',
		city = '',
		country = '',
		zipCode = '',
		phone1 = '',
		nickName = '',
		email1 = '',
		addressType = ADDRESS_SHIPPING_BILLING,
		addressId,
		primary,
		isOrgAddress,
	} = address;
	return {
		firstName,
		lastName,
		city,
		country,
		zipCode,
		phone1,
		nickName,
		email1,
		addressType,
		addressId,
		primary,
		addressLine1: addressLine?.at(0) ?? '',
		addressLine2: addressLine?.at(1) ?? '',
		addressLine3: addressLine?.at(2) ?? '',
		isOrgAddress,
	};
};

export const makePrintable = (address: BasicAddress | EditableAddress): PrintableAddress => {
	const { firstName, lastName, city, zipCode } = address;
	const fullName = [firstName, lastName].filter(Boolean).join(' ');
//	const physical = [city, state, zipCode].filter(Boolean).join(', ');
	const physical = [zipCode,city].filter(Boolean).join(' ');
	if (isEditableAddress(address)) {
		// editableAddress print to show validated address suggestion
		const { isOrgAddress, addressLine1, addressLine2, addressLine3, email1, phone1, ...addr } = address;
		const street = [addressLine1, addressLine2,addressLine3].filter(Boolean).join(' ');
		return { ...addr, addressLine: [addressLine1, addressLine2], physical , street };
	} else {
		const {addressLine } = address;
		const street = addressLine?[addressLine[0],addressLine[1],addressLine[2]].filter(Boolean).join(' '):"";
		return { ...address, fullName, physical , street };
	}
};

type AddressTypeKey = 'Shipping' | 'Billing' | 'Both';

export const AddressTypes: Record<AddressTypeKey, AddressType> = {
	Shipping: ADDRESS_SHIPPING,
	Billing: ADDRESS_BILLING,
	Both: ADDRESS_SHIPPING_BILLING,
};

export const maskCC = (card: string) => card.replace(REG_EX.CARD_NUMBER_MASK, '*');

export const getCityAndStreet = async(fields:Record<string,string>, storeId:string) => {

	if(REG_EX.ZIPCODE_POSTNL_SPACE.test(fields.zipCode)) {
		fields.zipCode = fields.zipCode.replace(' ','');
	}

	const resp = await getCityStreet(true)(storeId,{
		postalCode: fields.zipCode,
		houseNo: fields.addressLine2
	});
	return resp.exception ?
				{	
					error:{"AddressNotFound_POSTNL":"AddressNotFound_POSTNL"},
					addressLine1:"", city:""
				}
				:
				{
					addressLine1 : resp.street?resp.street:EMPTY_STRING,
					city : resp.city?resp.city:EMPTY_STRING
				}
}
export const isEditableAddress = (
	address: EditableAddress | BasicAddress | PrintableAddress | Address
): address is EditableAddress => (address as EditableAddress).addressLine1 !== undefined;


export const sortAddresses = (addresses: Address[] = []) => {
	return [...addresses].sort((a, b) => {
		// Primary address should be at the top
		if (a.primary === 'true') return -1;
		if (b.primary === 'true') return 1;

		// Remaining addresses sorted by newest address
		return Number(b.addressId) - Number(a.addressId);
	});
};